import { useState } from "react";
import { Button } from "../../components/reusableComponents/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "../../components/reusableComponents/Dialog";
import { Input } from "../../components/reusableComponents/Input";
import { Label } from "../../components/reusableComponents/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/reusableComponents/Popover";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../components/reusableComponents/RadioGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/reusableComponents/Select";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "../../components/reusableComponents/Calendar";

export function FactureModal({ open, onOpenChange, onSubmit }) {
  const [formData, setFormData] = useState({
    numero: "",
    client: "",
    montant: "",
    dateEcheance: new Date(),
    niveauRelance: 0,
    notesRelance: "",
    emailClient: "",
  });

  // Options pour les sélecteurs
  const clients = [
    "AMIRAL PRODUCTIONS",
    "SADESSAT LUBERSAC",
    "TRANSPORT VEYRSET",
    "DISTRIPRO",
    "MECATRONIX",
    "BATIPLUS",
    "NOVALYS",
    "MADIDAS THM",
    "FAON",
  ];

  const relanceModeles = [
    {
      niveau: 1,
      titre: "Premier rappel courtois",
      texte:
        "Cher client,\n\nNous nous permettons de vous rappeler que la facture #NUMERO d'un montant de #MONTANT est arrivée à échéance le #DATE. Merci de bien vouloir procéder au règlement dans les meilleurs délais.\n\nCordialement,\nService comptabilité",
    },
    {
      niveau: 2,
      titre: "Deuxième rappel",
      texte:
        "Cher client,\n\nMalgré notre précédent rappel, nous constatons que la facture #NUMERO d'un montant de #MONTANT dont l'échéance était le #DATE reste impayée. Nous vous prions de régulariser cette situation au plus vite.\n\nCordialement,\nService comptabilité",
    },
    {
      niveau: 3,
      titre: "Mise en demeure",
      texte:
        "Cher client,\n\nSuite à nos précédentes relances restées sans réponse, nous vous mettons en demeure de régler la facture #NUMERO d'un montant de #MONTANT échue depuis le #DATE sous 8 jours, faute de quoi nous serons contraints d'engager une procédure de recouvrement.\n\nCordialement,\nService juridique",
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRelanceChange = (niveau) => {
    setFormData((prev) => ({
      ...prev,
      niveauRelance: niveau,
    }));

    // Si niveau > 0, récupérer le modèle de texte correspondant
    if (niveau > 0) {
      const modele = relanceModeles.find((m) => m.niveau === niveau);
      if (modele) {
        let texte = modele.texte;
        texte = texte.replace("#NUMERO", formData.numero);
        texte = texte.replace(
          "#MONTANT",
          `${parseFloat(formData.montant || "0").toLocaleString("fr-FR")} €`,
        );
        texte = texte.replace(
          "#DATE",
          formData.dateEcheance
            ? format(formData.dateEcheance, "dd/MM/yyyy")
            : "",
        );

        handleInputChange("notesRelance", texte);
      }
    }
  };

  const handleSubmit = () => {
    const montantNumber = parseFloat(formData.montant || "0");

    onSubmit({
      numero: formData.numero,
      client: formData.client,
      montant: montantNumber,
      dateEcheance: format(formData.dateEcheance, "yyyy-MM-dd"),
      datePaiement: undefined,
      statut: "non soldée",
      factorise: false,
      niveauRelance: formData.niveauRelance,
    });

    // Si une relance est configurée, on pourrait envoyer un email
    if (formData.niveauRelance > 0 && formData.emailClient) {
      console.log(
        `Envoi d'un email de relance niveau ${formData.niveauRelance} à ${formData.emailClient}`,
      );
      console.log(`Contenu: ${formData.notesRelance}`);
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl scrollbar-none p-0 max-h-[1000px] overflow-scroll bg-white rounded-lg shadow-lg">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-800 py-6 px-8">
          <div className="flex flex-col">
            <DialogTitle className="text-2xl font-bold tracking-tight text-white">
              Relance de facture
            </DialogTitle>
            <p className="text-blue-100 mt-1">
              Configurez les options de relance pour une nouvelle facture
            </p>
          </div>
        </div>

        <div className="w-full p-8 space-y-6">
          {/* Options de relance */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center mb-4">
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900">
                Options de relance
              </h3>
            </div>

            <div className="mt-4">
              <RadioGroup
                value={formData.niveauRelance.toString()}
                onValueChange={(value) => handleRelanceChange(parseInt(value))}
                className="space-y-3"
              >
                <div className="flex items-center space-x-2 bg-blue-50 p-3 rounded-md border border-blue-200">
                  <RadioGroupItem value="1" id="relance-1" />
                  <Label htmlFor="relance-1" className="cursor-pointer">
                    <div className="font-medium text-blue-800">
                      Niveau 1 - Premier rappel courtois
                    </div>
                    <div className="text-sm text-blue-600">
                      Envoi d'un email de rappel simple, ton amical
                    </div>
                  </Label>
                </div>

                <div className="flex items-center space-x-2 bg-orange-50 p-3 rounded-md border border-orange-200">
                  <RadioGroupItem value="2" id="relance-2" />
                  <Label htmlFor="relance-2" className="cursor-pointer">
                    <div className="font-medium text-orange-800">
                      Niveau 2 - Deuxième rappel
                    </div>
                    <div className="text-sm text-orange-600">
                      Relance plus ferme avec rappel des échéances dépassées
                    </div>
                  </Label>
                </div>

                <div className="flex items-center space-x-2 bg-red-50 p-3 rounded-md border border-red-200">
                  <RadioGroupItem value="3" id="relance-3" />
                  <Label htmlFor="relance-3" className="cursor-pointer">
                    <div className="font-medium text-red-800">
                      Niveau 3 - Mise en demeure
                    </div>
                    <div className="text-sm text-red-600">
                      Dernier avertissement avant procédure de recouvrement
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {formData.niveauRelance > 0 && (
              <div className="mt-6 space-y-4 bg-gray-50 p-4 rounded-md">
                <div>
                  <Label
                    htmlFor="emailClient"
                    className="text-sm font-medium text-gray-700"
                  >
                    Email du client
                  </Label>
                  <Input
                    id="emailClient"
                    type="email"
                    value={formData.emailClient}
                    onChange={(e) =>
                      handleInputChange("emailClient", e.target.value)
                    }
                    placeholder="email@example.com"
                    className="mt-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="notesRelance"
                    className="text-sm font-medium text-gray-700"
                  >
                    Texte de la relance
                  </Label>
                  <div className="mt-1">
                    <textarea
                      id="notesRelance"
                      rows={6}
                      className="w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={formData.notesRelance}
                      onChange={(e) =>
                        handleInputChange("notesRelance", e.target.value)
                      }
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Ce texte sera envoyé par email au client comme relance de
                    paiement.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-8 py-5 bg-gray-50 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="mr-2"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
